import { Box, Typography } from "@mui/material";
import {
  Link as LinkIcon,
  TrackChanges as GoalsIcon,
  Chat as ConversationIcon,
  Phone as CallsIcon,
  Folder as ResourcesIcon,
  School as EnrollmentIcon,
} from "@mui/icons-material";
import { ReactNode } from "react";

import { SearchResult } from "@/api/generated.schemas";
import { NavigationItem } from "@/utils/navigationSearchItems";
import { SearchResultItem } from "./SearchResultItem";

interface SearchResultGroupProps {
  category: string;
  results: (SearchResult | NavigationItem)[];
  selectedIndex: number;
  groupStartIndex: number;
  onSelect: (index: number) => void;
  query: string;
}

/**
 * Get icon and label for a category.
 */
function getCategoryInfo(category: string): { icon: ReactNode; label: string } {
  const categoryMap: Record<string, { icon: ReactNode; label: string }> = {
    navigation: { icon: <LinkIcon fontSize="small" />, label: "Pages" },
    goal: { icon: <GoalsIcon fontSize="small" />, label: "Goals" },
    milestone: { icon: <GoalsIcon fontSize="small" />, label: "Milestones" },
    task: { icon: <GoalsIcon fontSize="small" />, label: "Tasks" },
    conversation: {
      icon: <ConversationIcon fontSize="small" />,
      label: "AI Coach",
    },
    transcript: { icon: <CallsIcon fontSize="small" />, label: "Calls" },
    resource: { icon: <ResourcesIcon fontSize="small" />, label: "Resources" },
    enrollment: {
      icon: <EnrollmentIcon fontSize="small" />,
      label: "Learning",
    },
  };

  return (
    categoryMap[category] || {
      icon: <LinkIcon fontSize="small" />,
      label: category.charAt(0).toUpperCase() + category.slice(1),
    }
  );
}

/**
 * Group of search results by category.
 */
export function SearchResultGroup({
  category,
  results,
  selectedIndex,
  groupStartIndex,
  onSelect,
  query,
}: SearchResultGroupProps) {
  if (results.length === 0) {
    return null;
  }

  const { icon, label } = getCategoryInfo(category);

  return (
    <Box>
      {/* Category header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          bgcolor: "grey.50",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ color: "text.secondary" }}>{icon}</Box>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {label}
        </Typography>
      </Box>

      {/* Results */}
      {results.map((result, index) => {
        const globalIndex = groupStartIndex + index;
        return (
          <SearchResultItem
            key={"id" in result ? result.id : `nav-${index}`}
            result={result}
            isSelected={selectedIndex === globalIndex}
            onSelect={() => onSelect(globalIndex)}
            query={query}
          />
        );
      })}
    </Box>
  );
}
